import React, { useEffect, useMemo, useRef } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import KeyboardArrowUpRoundedIcon from "@mui/icons-material/KeyboardArrowUpRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import type { FindHighlightResult } from "./findHighlights/types";

type FindHighlightsResultsSidebarProps = {
    onBack: () => void;
    highlights?: FindHighlightResult[];
    selectedHighlightId?: string | null;
    onSelectHighlight?: (highlightId: string) => void;
    onDiscardAll?: () => void;
    onDone?: () => void;
};

const formatDurationLabel = (seconds: number): string => {
    const normalized = Number.isFinite(seconds) ? Math.max(0, Math.round(seconds)) : 0;
    return `${normalized} ${normalized === 1 ? "sec" : "secs"}`;
};

const FindHighlightsResultsSidebar: React.FC<FindHighlightsResultsSidebarProps> = ({
    onBack,
    highlights,
    selectedHighlightId,
    onSelectHighlight,
    onDiscardAll,
    onDone,
}) => {
    const rows = useMemo(() => (Array.isArray(highlights) ? highlights : []), [highlights]);
    const effectiveSelectedIndex = useMemo(() => {
        if (rows.length === 0) return -1;
        const selectedIndex = rows.findIndex((h) => h.id === selectedHighlightId);
        return selectedIndex >= 0 ? selectedIndex : 0;
    }, [rows, selectedHighlightId]);
    const selectedCardRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!selectedCardRef.current) return;
        selectedCardRef.current.scrollIntoView({
            block: "nearest",
            behavior: "smooth",
        });
    }, [effectiveSelectedIndex]);

    const handleSelectIndex = (index: number) => {
        if (index < 0 || index >= rows.length) return;
        const nextHighlight = rows[index];
        if (!nextHighlight) return;
        onSelectHighlight?.(nextHighlight.id);
    };

    return (
        <Box
            sx={{
                height: "100%",
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: "10px",
                p: "3px",
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "10px",
                    marginLeft: "8px",
                    pr: "4px",
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <IconButton size="small" onClick={onBack} sx={{ p: 0.5 }}>
                        <ArrowBackRoundedIcon fontSize="small" />
                    </IconButton>
                    <Typography sx={{ fontSize: 15, fontWeight: 600, color: "var(--app-text-primary)" }}>
                        Find highlights
                    </Typography>
                </Box>

                {rows.length > 0 ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: "2px", flexShrink: 0 }}>
                        <Typography
                            sx={{
                                fontSize: 13,
                                fontWeight: 600,
                                color: "var(--app-text-primary)",
                                minWidth: 44,
                                textAlign: "center",
                            }}
                        >
                            {effectiveSelectedIndex + 1} / {rows.length}
                        </Typography>
                        <IconButton
                            size="small"
                            onClick={() => handleSelectIndex(effectiveSelectedIndex - 1)}
                            disabled={effectiveSelectedIndex <= 0}
                            sx={{ width: 28, height: 28 }}
                        >
                            <KeyboardArrowUpRoundedIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                            size="small"
                            onClick={() => handleSelectIndex(effectiveSelectedIndex + 1)}
                            disabled={effectiveSelectedIndex >= rows.length - 1}
                            sx={{ width: 28, height: 28 }}
                        >
                            <KeyboardArrowDownRoundedIcon fontSize="small" />
                        </IconButton>
                    </Box>
                ) : null}
            </Box>

            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: "auto",
                    pr: "2px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                    scrollbarWidth: "thin",
                }}
            >
                {rows.length > 0 ? (
                    rows.map((highlight, index) => {
                        const isSelected = index === effectiveSelectedIndex;

                        return (
                            <Box
                                key={highlight.id}
                                ref={isSelected ? selectedCardRef : null}
                                role="button"
                                tabIndex={0}
                                onClick={() => onSelectHighlight?.(highlight.id)}
                                onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
                                    if (event.key === "Enter" || event.key === " ") {
                                        event.preventDefault();
                                        onSelectHighlight?.(highlight.id);
                                    }
                                }}
                                sx={{
                                    backgroundColor: "var(--app-bg-surface)",
                                    border: "1px solid",
                                    borderColor: isSelected ? "#60A5FA" : "var(--app-border-subtle)",
                                    borderRadius: "10px",
                                    px: "10px",
                                    py: "10px",
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "6px",
                                    cursor: "pointer",
                                    transition: "border-color 0.12s ease, box-shadow 0.12s ease",
                                    boxShadow: isSelected
                                        ? "inset 0 0 0 1px rgba(96, 165, 250, 0.35)"
                                        : "none",
                                    outline: "none",
                                    "&:hover": {
                                        borderColor: isSelected ? "#60A5FA" : "var(--app-border-muted)",
                                    },
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: 12.5,
                                        lineHeight: 1.45,
                                        color: "var(--app-text-primary)",
                                        display: "-webkit-box",
                                        WebkitLineClamp: 4,
                                        WebkitBoxOrient: "vertical",
                                        overflow: "hidden",
                                    }}
                                >
                                    {highlight.previewText}
                                </Typography>

                                <Typography sx={{ fontSize: 12.5, color: "var(--app-text-muted)", pt: "2px" }}>
                                    {formatDurationLabel(highlight.durationSec)}
                                </Typography>
                            </Box>
                        );
                    })
                ) : (
                    <Box
                        sx={{
                            backgroundColor: "var(--app-bg-surface)",
                            border: "1px solid var(--app-border-subtle)",
                            borderRadius: "10px",
                            px: "10px",
                            py: "10px",
                        }}
                    >
                        <Typography sx={{ fontSize: 13, color: "var(--app-text-muted)", lineHeight: 1.5 }}>
                            No highlights found yet.
                        </Typography>
                    </Box>
                )}
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: "4px", pt: "2px" }}>
                <Button
                    onClick={onDiscardAll}
                    disabled={rows.length === 0}
                    startIcon={<CloseRoundedIcon sx={{ fontSize: 16 }} />}
                    sx={{
                        justifyContent: "flex-start",
                        textTransform: "none",
                        color: "var(--app-text-primary)",
                        fontSize: 13,
                        fontWeight: 500,
                        borderRadius: "8px",
                        minHeight: 36,
                        px: "10px",
                        "&:hover": {
                            backgroundColor: "rgba(17, 24, 39, 0.04)",
                        },
                    }}
                >
                    Discard all
                </Button>

                <Button
                    onClick={onDone}
                    disabled={rows.length === 0}
                    startIcon={<CheckRoundedIcon sx={{ fontSize: 16 }} />}
                    sx={{
                        justifyContent: "flex-start",
                        textTransform: "none",
                        color: "var(--app-text-primary)",
                        fontSize: 13,
                        fontWeight: 500,
                        borderRadius: "8px",
                        minHeight: 36,
                        px: "10px",
                        "&:hover": {
                            backgroundColor: "rgba(17, 24, 39, 0.04)",
                        },
                    }}
                >
                    Done
                </Button>
            </Box>
        </Box>
    );
};

export default FindHighlightsResultsSidebar;
